import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../../utils/api';
import { isParkedForReview } from './parkedForReview';

// How long Skip/Back must rest before the quote under the cursor is shown.
// Every quote shown fires ~15 Worker calls and 8 upstream item reads, so
// stepping through the queue quickly (a runbook helper clicked Skip every
// 60 ms through 51 quotes) sent ~850 calls in 14 s and tripped the Prism rate
// limit (2026-10-04). A lone step is not held; only the steps that follow
// within this window are.
export const SETTLE_MS = 300;

export function useAssessmentQueue() {
    const [queue, setQueue] = useState([]);
    // `cursor` is the quote on screen: every action reads it. `target` is where
    // Skip/Back have moved to; `cursor` follows once the stepping settles.
    const [cursor, setCursor] = useState(0);
    const [target, setTarget] = useState(0);
    const lastStepAt = useRef(-Infinity);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            // ?view=assessment-queue opts this read into the reviewer scope (a
            // reviewer sees only their assigned quotes here). A bare /api/deals is
            // shared with Quote Requested, so the worker must NOT scope on the bare
            // call, or that page renders empty for a reviewer (2026-09-22).
            const res = await api.get('/api/deals?view=assessment-queue');
            // Priced quotes, PLUS engine parks — the quotes the engine gave up on
            // (NO_MARKET_DATA, IDENTITY_MATCH_FAILED) need an assessor MOST, and
            // filtering on 'completed' alone hid them (their D1 status is 'error';
            // live: TI-81F79, 2026-07-28). Customer-waiting parks stay out.
            // Second-review order (spec docs/archive/202609/specs_20260916_quote_review.md): a
            // reviewed quote awaiting the admin's decision floats to the top, an
            // assigned-out quote drops to the bottom, everything else keeps its
            // oldest-first createdate order. A reviewer only ever sees their own
            // assigned quotes, so for them every row is rank 2 and this reduces to
            // the same createdate sort.
            const reviewRank = (d) => d.review_status === 'reviewed' ? 0 : (d.review_status === 'assigned' ? 2 : 1);
            const list = (res.deals || [])
                .filter(d => d.pricing_log_status === 'completed' || isParkedForReview(d))
                .sort((a, b) => reviewRank(a) - reviewRank(b)
                    || String(a.createdate).localeCompare(String(b.createdate)));
            setQueue(list);
            setCursor(0);
            setTarget(0);
        } catch (e) {
            setError(e.message || 'Failed to load queue');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (target === cursor) return undefined;
        const now = Date.now();
        const rapid = now - lastStepAt.current < SETTLE_MS;
        lastStepAt.current = now;
        if (!rapid) { setCursor(target); return undefined; }
        const timer = setTimeout(() => setCursor(target), SETTLE_MS);
        return () => clearTimeout(timer);
    }, [target, cursor]);

    // Post-action advance: the current deal has been actioned (moved to another
    // HubSpot stage) so it leaves the queue. The cursor stays put to land on the
    // deal that shifts into its place; if it was the last one, wrap to the start
    // so any earlier skipped deals get picked up.
    const next = useCallback(() => {
        setQueue(q => {
            if (!q.length) return q;
            const copy = q.slice();
            copy.splice(cursor, 1);
            return copy;
        });
        const remaining = queue.length - 1;
        const landed = remaining <= 0 || cursor >= remaining ? 0 : cursor;
        setCursor(landed);
        setTarget(landed);  // a step that has not landed yet is dropped
    }, [cursor, queue.length]);
    const back = useCallback(() => setTarget(t => Math.max(t - 1, 0)), []);
    // Skip = advance to the next quote (linear); Back revisits the previous one.
    const skip = useCallback(() => setTarget(t => Math.min(t + 1, Math.max(queue.length - 1, 0))), [queue.length]);
    const updateCurrent = useCallback((patch) => setQueue(q => {
        const copy = q.slice();
        if (copy[cursor]) copy[cursor] = { ...copy[cursor], ...patch };
        return copy;
    }), [cursor]);

    // Other quotes from the same customer still in this queue — assessors see
    // them as a "+N more in queue" flag so multi-quote customers get considered
    // as a whole. Null emails never match each other.
    const currentEmail = queue[cursor]?.seller_email?.toLowerCase() || null;
    const siblingCount = currentEmail
        ? queue.filter((d, i) => i !== cursor && d.seller_email?.toLowerCase() === currentEmail).length
        : 0;

    return {
        current: queue[cursor] || null,
        // The counter follows the steps at once, ahead of the quote shown.
        position: queue.length ? target + 1 : 0,
        total: queue.length,
        siblingCount,
        loading, error, next, back, skip, refresh, updateCurrent,
    };
}
